import { apiFetch } from "./apiClient";
import { BlockType, ChartBlock, UIMessage } from "../components/Chat/Chat.types";

/**
 * POST the rendered HTML of a single message to the backend PDF endpoint.
 */
export async function exportMessageToPdf(
	html: string,
	chatId: string,
	messageId: string,
): Promise<void> {
	const response = await apiFetch(`/api/v3/chats/${chatId}/export-pdf`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ html_content: html }),
	});

	if (!response.ok) {
		const err = await response.text().catch(() => "Unknown error");
		throw new Error(`PDF export failed: ${err}`);
	}

	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `message_${messageId.slice(0, 8)}_${new Date().toISOString().split("T")[0]}.pdf`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Chat-level PDF export
// ---------------------------------------------------------------------------

function escapeHtml(text: string): string {
	const el = document.createElement("span");
	el.textContent = text;
	return el.innerHTML;
}

function sanitizeFilename(name: string): string {
	return name
		.replace(/[^a-zA-Z0-9 _-]/g, "")
		.trim()
		.replace(/\s+/g, "_")
		.slice(0, 80);
}

/**
 * Build an HTML document from every message in the chat container and send it
 * to the backend for Playwright PDF rendering.  Thinking content is excluded —
 * only `[data-export-answer]` innerHTML is captured for assistant messages.
 */
export async function exportChatToPdf(
	container: HTMLElement,
	chatId: string,
	chatTitle: string,
	chatCreatedAt?: number,
	messages: UIMessage[] = [],
): Promise<void> {
	const messageEls = container.querySelectorAll<HTMLElement>(
		"[data-message-id]",
	);

	// Build chart_id → ChartBlock lookup across all messages.
	// Charts may be unmounted (IntersectionObserver) or rendered as <canvas>
	// (stripped by sanitiser), so we render them from persisted chart_data.
	const chartBlocksById = new Map<string, ChartBlock>();
	const chartBlocksByMessageOrder = new Map<string, ChartBlock[]>();
	for (const m of messages) {
		const ordered: ChartBlock[] = [];
		for (const block of m.content_blocks) {
			if (block.type === BlockType.CHART) {
				const cb = block as ChartBlock;
				ordered.push(cb);
				if (cb.chart_id) chartBlocksById.set(cb.chart_id, cb);
			}
		}
		if (ordered.length > 0) chartBlocksByMessageOrder.set(m.id, ordered);
	}

	const hasCharts = chartBlocksById.size > 0 || chartBlocksByMessageOrder.size > 0;
	const renderChartToImage = hasCharts
		? (await import("./chartExport")).renderChartToImage
		: null;

	const dateOpts: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: "long",
		day: "numeric",
	};
	const exportDate = new Date().toLocaleDateString("en-US", dateOpts);

	let dateLine = `Exported on ${exportDate}`;
	if (chatCreatedAt) {
		const createdDate = new Date(chatCreatedAt).toLocaleDateString(
			"en-US",
			dateOpts,
		);
		dateLine = `Created on ${createdDate} &middot; Exported on ${exportDate}`;
	}

	const sections: string[] = [
		`<div class="chat-header">` +
			`<h1>${escapeHtml(chatTitle || "Chat Export")}</h1>` +
			`<p class="chat-date">${dateLine}</p>` +
			`</div>`,
	];

	messageEls.forEach((el) => {
		const role = el.getAttribute("data-role");

		if (role === "user") {
			const contentDiv = el.querySelector("[data-message-content]");
			const text = (contentDiv ?? el).textContent ?? "";
			sections.push(
				`<div class="chat-message user-message">` +
					`<div class="message-role">You</div>` +
					`<div class="message-body">${escapeHtml(text)}</div>` +
					`</div>`,
			);
		} else {
			const answerEl = el.querySelector("[data-export-answer]");
			if (!answerEl) return;
			const messageId = el.getAttribute("data-message-id") ?? "";
			// Markdown tables now render as plain <table> — no hidden
			// PDF-shadow table or Univer wrapper to clean up.
			const tmp = document.createElement("div");
			tmp.innerHTML = answerEl.innerHTML;

			// Replace chart placeholders with rendered images from persisted data.
			// Stable lookup by chart_id; fall back to positional order per message.
			if (renderChartToImage) {
				const orderedForMsg = chartBlocksByMessageOrder.get(messageId) ?? [];
				let positionalIdx = 0;
				tmp.querySelectorAll("[data-chart-block]").forEach((placeholder) => {
					const id = placeholder.getAttribute("data-chart-block") ?? "";
					const chartBlock =
						(id && chartBlocksById.get(id)) || orderedForMsg[positionalIdx++];
					if (!chartBlock) {
						placeholder.remove();
						return;
					}
					const dataUrl = renderChartToImage(
						chartBlock.chart_data,
						chartBlock.chart_type,
						chartBlock.title,
					);
					if (dataUrl) {
						const img = document.createElement("img");
						img.src = dataUrl;
						img.style.maxWidth = "100%";
						img.style.margin = "12px 0";
						placeholder.replaceWith(img);
					} else {
						placeholder.remove();
					}
				});
			}

			sections.push(
				`<div class="chat-message assistant-message">` +
					`<div class="message-role">Doppler</div>` +
					`<div class="markdown-content">${tmp.innerHTML}</div>` +
					`</div>`,
			);
		}
	});

	const bodyHtml = sections.join("\n");

	const response = await apiFetch(`/api/v3/chats/${chatId}/export-pdf`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ html_content: bodyHtml, is_chat_export: true }),
	});

	if (!response.ok) {
		const err = await response.text().catch(() => "Unknown error");
		throw new Error(`Chat PDF export failed: ${err}`);
	}

	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	const dateSuffix = new Date().toISOString().split("T")[0];
	const fileLabel = chatTitle ? sanitizeFilename(chatTitle) : `Chat_${chatId}`;
	a.download = `${fileLabel}_${dateSuffix}.pdf`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}
